import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import MarketingTopBar from "@/components/MarketingTopBar";
import FooterLinks from "@/components/FooterLinks";
import { Wrench, Video, Check, X, UserRound, Star } from "lucide-react";

const BG_IMAGE =
  "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/5363768eda87f48c_PLSR%20FRS%20Lite%20Chassis%20-%20SRC%20Home%20BG%20%28resize%202%29.webp";

const POWERED_BY =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_throttle-hub-6/artifacts/krvfdgqm_Powered%20by%20PLSR%20%28cropped%29.png";

// "user-star" composite (lucide has no UserStar export)
function UserStarIcon({ className }) {
  return (
    <span className={`relative inline-flex items-center justify-center ${className}`}>
      <UserRound className="h-full w-full" />
      <Star className="absolute -right-1 -top-1 h-2.5 w-2.5 fill-current" />
    </span>
  );
}

const CARDS = [
  {
    key: "founding_member",
    icon: UserStarIcon,
    name: "Founding Member",
    tag: "Launch Only",
    accent: "text-amber-300",
    blurb:
      "For early supporters who join us right out of the gate. You help shape the platform while it is brand new (and yes, you may run into the odd bug). In return you keep a permanent Founding Member badge for as long as you are a member.",
    perks: [
      "Permanent Founding Member badge on your profile",
      "Early access and a direct line to influence new features",
      "Special perks reserved for founders, offered over time by our team",
      "50% extra (1.5x) points on the platform forever, earning rewards and products much faster than regular membership after launch",
      "Bonus entries to every giveaway we run on the platform forever",
      "Free to join, available only during launch",
    ],
  },
  {
    key: "sim_racing_expert",
    icon: Wrench,
    name: "Sim Racing Expert",
    tag: "Verified know-how",
    foundingDuringLaunch: true,
    accent: "text-primary",
    blurb:
      "For coaches, setup builders, league admins and seasoned racers. Tell us a little about your background so we can recognise your expertise across the community.",
    perks: [
      "Free to join",
      "Permanent Sim Racing Expert badge on your profile",
      "Stand out when you sell setups, coaching, or services",
      "Earnings from every question answered or guides posted on the platform",
      "Priority consideration for community spotlights",
      "Share a short bio and your online profiles when you apply",
    ],
  },
  {
    key: "content_creator",
    icon: Video,
    name: "Content Creator",
    tag: "Streamers and creators",
    foundingDuringLaunch: true,
    accent: "text-primary",
    blurb:
      "For streamers, YouTubers and anyone building a sim racing audience. Link your channels so we can connect you with the right opportunities.",
    perks: [
      "Free to join",
      "Permanent Content Creator badge on your profile",
      "Earnings from every question answered or guides posted on the platform",
      "Earnings from content and engagement shared on the platform, including content posted on other social media or streaming platforms",
      "Collaboration and promotion opportunities",
      "A trusted identity when you post and sell",
      "Share a short bio and your online profiles when you apply",
    ],
  },
];

export default function MembershipBenefits() {
  const navigate = useNavigate();
  const [foundingEnabled, setFoundingEnabled] = useState(true);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    authAPI
      .getSignupConfig()
      .then((r) => setFoundingEnabled(!!r.data?.founding_member_enabled))
      .catch(() => {});
  }, []);

  const continueSignup = () => {
    navigate(selected ? `/register?category=${selected}` : "/register");
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-background text-foreground">
      {/* Blurred background image + dark overlay */}
      <div
        className="pointer-events-none fixed inset-0 -z-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_IMAGE})`, filter: "blur(14px)", transform: "scale(1.1)" }}
      />
      <div className="pointer-events-none fixed inset-0 -z-10" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} />

      <MarketingTopBar compact />

      <main className="relative flex-1 px-4 pb-16 pt-28 sm:px-6 sm:pt-32">
        <div className="mx-auto max-w-6xl">
          {/* Close / back to sign up */}
          <button
            type="button"
            onClick={() => navigate("/register")}
            className="absolute right-4 top-24 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/60 text-foreground backdrop-blur transition-colors hover:border-primary hover:text-primary sm:right-6 sm:top-28"
            aria-label="Close"
            data-testid="membership-close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="mb-10 flex flex-col items-center text-center">
            <h1 className="font-display text-4xl font-bold uppercase tracking-wide sm:text-5xl lg:text-6xl">
              Choose how you join
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Every member picks a category when they sign up. It gives you a permanent badge on your profile so the
              community (and our team) know the value you bring. Here is what each one means.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-white">
              Every membership is 100% free, including Experts and Creators.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {CARDS.map((c) => {
              const Icon = c.icon;
              const closed = c.key === "founding_member" && !foundingEnabled;
              const isSelected = selected === c.key;
              return (
                <button
                  type="button"
                  key={c.key}
                  disabled={closed}
                  onClick={() => setSelected(c.key)}
                  data-testid={`membership-card-${c.key}`}
                  aria-pressed={isSelected}
                  className={`group relative flex flex-col rounded-2xl border-2 bg-card/80 p-6 text-left backdrop-blur transition-all duration-200 ${
                    closed
                      ? "cursor-not-allowed border-border opacity-60"
                      : isSelected
                      ? "border-primary shadow-xl shadow-primary/20 -translate-y-1"
                      : "border-border hover:-translate-y-1 hover:border-primary/60 hover:shadow-xl"
                  }`}
                >
                  {isSelected && (
                    <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground" data-testid={`membership-selected-${c.key}`}>
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                  <div className="mb-4 flex items-center justify-between">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl bg-secondary ${c.accent}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${c.accent}`}>
                      {closed ? "Closed" : c.tag}
                    </span>
                  </div>
                  <h2 className="font-display text-xl font-bold uppercase">{c.name}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{c.blurb}</p>
                  <ul className="mt-4 space-y-2">
                    {c.perks.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  {c.foundingDuringLaunch && foundingEnabled && (
                    <p className="mt-auto pt-4">
                      <span className="block rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs font-medium text-amber-300">
                        Join during launch and you also get Founding Member status and all its perks, for free.
                      </span>
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {!foundingEnabled && (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Founding membership is no longer open. You can still join as a Sim Racing Expert or Content Creator, or
              continue as a regular member.
            </p>
          )}

          <div className="mt-10 flex flex-col items-center gap-3">
            <Button
              onClick={continueSignup}
              className="bg-primary px-8 text-primary-foreground hover:bg-primary/90"
              data-testid="membership-to-register"
            >
              Continue sign up
            </Button>
          </div>
        </div>
      </main>

      <footer className="relative mx-auto flex w-full max-w-7xl flex-col items-center gap-4 px-4 py-10 sm:flex-row sm:justify-between sm:px-6">
        <img src={POWERED_BY} alt="Powered by PLSR" className="h-10 w-auto opacity-90" draggable={false} />
        <FooterLinks tone="dark" align="center" />
      </footer>
    </div>
  );
}
